/***
 * Created on 2025/4/30
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React from 'react';
import {useTranslation} from "react-i18next";
import {useFloating, offset, flip, shift, autoUpdate} from '@floating-ui/react-dom';
import {normalizeSpaceSize} from "../../service/utils";

function StandardPopover({t, state}) {
    return (
        <div className="popover-menu">
            <div className="usage-amount__hint">
                <span className="usage-amount__hint__marker usage-amount__hint__marker--storage"></span>
                <span className="usage-amount__hint__label"><span className="font-light">myQNAPcloud</span> <span className="font-medium">{t('@Storage@')}</span>:</span>
                <span className="usage-amount__hint__value">{normalizeSpaceSize(state?.data?.row_data?.quota?.used)}</span>
            </div>
        </div>
    )
}

function ObjectStoragePopover({t, state}) {
    return (
        <div className="popover-menu">
            <div className="usage-amount__hint">
                <span className="usage-amount__hint__marker usage-amount__hint__marker--obj-storage"></span>
                <span className="usage-amount__hint__label"><span className="font-light">myQNAPcloud</span> <span className="font-medium">{t('@Object@')}</span>:</span>
                <span className="usage-amount__hint__value">
                    {normalizeSpaceSize(state?.data?.row_data?.quota?.used - (state?.data?.row_data?.quota?.used_by?.object || 0))}
                </span>
            </div>
            <div className="usage-amount__hint__bucket-info">
                {t('@Data updates daily and may not reflect recent changes.@')}
            </div>
            <div className="usage-amount__hint">
                <span className="usage-amount__hint__marker usage-amount__hint__marker--storage"></span>
                <span className="usage-amount__hint__label"><span className="font-light">myQNAPcloud</span> <span className="font-medium">{t('@Storage@')}</span>:</span>
                <span className="usage-amount__hint__value">{normalizeSpaceSize(state?.data?.row_data?.quota?.used)}</span>
            </div>
        </div>
    );
}

function WarningPopover({t, state}) {
    return (
        <div className="popover-menu warning-msg">
            <div className="usage-amount__hint">
                <span className="usage-amount__hint__marker usage-amount__hint__marker--storage"></span>
                <span className="usage-amount__hint__label"><span className="font-light">myQNAPcloud</span> <span className="font-medium">{t('@Storage@')}</span>:</span>
                <span className="usage-amount__hint__value">{normalizeSpaceSize(state?.data?.row_data?.quota?.used)}</span>
            </div>
            <div className="usage-amount__divider"></div>
            <p>{t('@You storage space is almost full, you can either upgrade the subscription plan or clean up some space.@')}&nbsp;</p>
            <a href="#">{t('@Upgrade your plan@')}</a>
        </div>
    )
}

function ErrorPopover({t, state}) {
    return (
        <div className="popover-menu error-msg">
            <div className="usage-amount__hint">
                <span className="usage-amount__hint__marker usage-amount__hint__marker--storage"></span>
                <span className="usage-amount__hint__label"><span className="font-light">myQNAPcloud</span> <span className="font-medium">{t('@Storage@')}</span>:</span>
                <span className="usage-amount__hint__value">{normalizeSpaceSize(state?.data?.row_data?.quota?.used)}</span>
            </div>
            <div className="usage-amount__divider"></div>
            <p>{t('@We recommend to upgrade your {{space_name}} space immediately. Your sync through Hybrid Backup Sync has stopped, and you cannot upload any items through HybridMount nor myQNAPcloud Portal.@',
                {space_name: t(`@${state?.data?.row_data?.region}@`)})}&nbsp;
            </p>
            <a href="#">{t('@Upgrade your plan@')}</a>
        </div>
    )
}

function NoAccessPopover({t, state}) {
    return (
        <div className="popover-menu error-msg">
            <div className="usage-amount__hint">
                <span className="usage-amount__hint__marker usage-amount__hint__marker--storage"></span>
                <span className="usage-amount__hint__label"><span className="font-light">myQNAPcloud</span> <span className="font-medium">{t('@Storage@')}</span>:</span>
                <span className="usage-amount__hint__value">{normalizeSpaceSize(state?.data?.row_data?.quota?.used)}</span>
            </div>
            <div className="usage-amount__divider"></div>
            <p>{t('@You storage space has become limited access, please check your subscription status. You cannot store any new items and items will be deleted after 30 days from limited access.@')}&nbsp;</p>
            <a href="#">{t('@Upgrade your plan@')}</a>
        </div>
    )
}

function CloudSpaceUsageWarningPopoverComponent({state, scheduleHide, cancelHide}) {
    const {t} = useTranslation();

    const {refs, floatingStyles} = useFloating({
        elements: {
            reference: state?.data?.trigger_element || null,
        },
        strategy: 'fixed',
        placement: 'bottom-start',
        middleware: [
            offset(4),
            flip(),
            shift({padding: 8}),
        ],
        whileElementsMounted: (reference, floating, update) =>
            autoUpdate(reference, floating, update, {
                elementResize: false,
                layoutShift: false,
            }),
    });

    if (!state?.show_popover) return null;

    const isInteractive = ['warning', 'error', 'no_access'].includes(state.data?.popover_type);

    return (
        <div
            ref={refs.setFloating}
            className="popover popover--fixed popover--storage-usage"
            style={floatingStyles}
            onMouseEnter={isInteractive ? cancelHide : undefined}
            onMouseLeave={isInteractive ? () => scheduleHide(150) : undefined}
        >
            {state.data?.popover_type === 'standard' && <StandardPopover t={t} state={state} />}
            {state.data?.popover_type === 'object_storage' && <ObjectStoragePopover t={t} state={state} />}
            {state.data?.popover_type === 'warning' && <WarningPopover t={t} state={state} />}
            {state.data?.popover_type === 'error' && <ErrorPopover t={t} state={state} />}
            {state.data?.popover_type === 'no_access' && <NoAccessPopover t={t} state={state} />}
        </div>
    );
}

export default CloudSpaceUsageWarningPopoverComponent;
